import { memo, type ReactNode, useCallback, useState } from 'react';

import { WorkspaceAgentClarifyMessage } from '@air/component-agent-clarify';
import { Button } from '@air/primitive-button';

import { WorkspaceAgentGuidedVariantOutputOptions } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantOutputOptions';
import { WorkspaceAgentGuidedVariantSelectBoardPopover } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/WorkspaceAgentGuidedVariantSelectBoardPopover';
import { isWorkspaceAgentPanelHotkeyEvent } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentPanelHotkeyEvent';
import { type WorkspaceAgentGuidedVariantOutput } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  getWorkspaceAgentGuidedVariantClarifySaveOptions,
  getWorkspaceAgentGuidedVariantSelectBoardOptionIndex,
  isWorkspaceAgentGuidedVariantSaveOptionId,
  WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_QUESTION,
  WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_BOARD_SAVE_OPTION,
  type WorkspaceAgentGuidedVariantSaveLocationOptions,
  type WorkspaceAgentGuidedVariantSaveOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

export type WorkspaceAgentGuidedVariantSaveLocationProps = {
  allowsFormat: boolean;
  allowsJpeg: boolean;
  onDiscard: () => void;
  onOutputChange: (output: WorkspaceAgentGuidedVariantOutput) => void;
  onSelect: (optionId: WorkspaceAgentGuidedVariantSaveOptionId) => void;
  options: WorkspaceAgentGuidedVariantSaveLocationOptions;
  output: WorkspaceAgentGuidedVariantOutput;
  selectBoardMenu: ReactNode;
};

export const WorkspaceAgentGuidedVariantSaveLocation = memo(
  ({
    allowsFormat,
    allowsJpeg,
    onDiscard,
    onOutputChange,
    onSelect,
    options,
    output,
    selectBoardMenu,
  }: WorkspaceAgentGuidedVariantSaveLocationProps) => {
    const clarifyOptions = getWorkspaceAgentGuidedVariantClarifySaveOptions(options);
    const selectBoardIndex = getWorkspaceAgentGuidedVariantSelectBoardOptionIndex(options);

    const [isOutputDockOpen, setIsOutputDockOpen] = useState(false);

    const handleSelect = useCallback(
      (optionId: string) => {
        if (isWorkspaceAgentGuidedVariantSaveOptionId(optionId)) {
          onSelect(optionId);
        }
      },
      [onSelect],
    );

    return (
      <WorkspaceAgentClarifyMessage
        actionButtons={
          <div className="flex min-w-0 flex-col gap-2">
            <WorkspaceAgentGuidedVariantSelectBoardPopover
              index={selectBoardIndex}
              isDisabled={isOutputDockOpen}
              label={WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_BOARD_SAVE_OPTION.label}
              optionId={WORKSPACE_AGENT_GUIDED_VARIANT_SELECT_BOARD_SAVE_OPTION.id}
            >
              {selectBoardMenu}
            </WorkspaceAgentGuidedVariantSelectBoardPopover>
            <div className="min-w-0">
              <WorkspaceAgentGuidedVariantOutputOptions
                allowsFormat={allowsFormat}
                allowsJpeg={allowsJpeg}
                onChange={onOutputChange}
                onOpenChange={setIsOutputDockOpen}
                output={output}
              >
                <Button
                  appearance="ghost"
                  color="red"
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_DISCARD"
                  onClick={onDiscard}
                  size="small"
                >
                  Discard
                </Button>
              </WorkspaceAgentGuidedVariantOutputOptions>
            </div>
          </div>
        }
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_LOCATION"
        enableNumberHotkeys
        enableNumberHotkeysOnContentEditable
        onSelect={isOutputDockOpen ? undefined : handleSelect}
        optionTestId="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_OPTION"
        options={clarifyOptions}
        question={WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_QUESTION}
        questionTestId="WORKSPACE_AGENT_GUIDED_VARIANT_SAVE_QUESTION"
        shouldHandleNumberHotkey={isWorkspaceAgentPanelHotkeyEvent}
      />
    );
  },
);

WorkspaceAgentGuidedVariantSaveLocation.displayName = 'WorkspaceAgentGuidedVariantSaveLocation';
